import * as React from 'react';
import * as _ from 'underscore';

import { TabStyles } from '@tableau/widgets';

import { Messages } from '../../../codegen/Localize';
import { CommonStyles } from '../../../common/react/CommonStyles';
import { cssClass } from '../../../style';
import { L10n } from '../../../ts/L10n/L10n';
import { ILicenseConsumptionInfo } from '../../../ts/ServerApi/types/ILicenseConsumptionInfo';
import { LicensesService } from '../../../ts/services/LicensesService';

export interface LicenseInfoBarProps {
  readonly hasSiteLicensingInfo: boolean;
  readonly licensesService: LicensesService;
}

export function LicenseInfoBar(props: LicenseInfoBarProps) {
  const [isFetching, setIsFetching] = React.useState(true);
  const [licenseInfo, setLicenseInfo] = React.useState<ILicenseConsumptionInfo>(undefined);
  const [isCoreBasedLicense, setIsCoreBasedLicense] = React.useState(false);

  React.useEffect(() => {
    let didUnmount = false;
    const fetch = async () => {
      const licenseInfoPromise = props.hasSiteLicensingInfo ?
        props.licensesService.fetchSiteScopedCapacityInfoForInfoBar() :
        props.licensesService.fetchCapacityInfoForInfoBar();
      const licenseInfo = await licenseInfoPromise;
      if (!didUnmount) {
        setIsFetching(false);
        setLicenseInfo(licenseInfo);
        setIsCoreBasedLicense(licenseInfo.totalCoreLicenses > 0);
      }
    };

    fetch();

    return () => {
      didUnmount = true;
    };
  }, []);

  return (
    <div className={LicenseInfoBarContainer}>
      { !isFetching &&
      <>
        <span className={LicenseInfoBarItem}>
          {Messages.licenseCreatorCounts({
            USED_COUNT_LOC: _.isUndefined(licenseInfo) ? '' : L10n.localizedCount(licenseInfo.usedCreatorUsers),
            TOTAL_COUNT_LOC: _.isUndefined(licenseInfo) ? '' : L10n.localizedCount(licenseInfo.totalCreatorUsers)
          })}
        </span>
        <span className={LicenseInfoBarItem}>
          {isCoreBasedLicense ? Messages.licenseExplorerUnlimited() :
            Messages.licenseExplorerCounts({
            USED_COUNT_LOC: _.isUndefined(licenseInfo) ? '' : L10n.localizedCount(licenseInfo.usedExplorerUsers),
            TOTAL_COUNT_LOC: _.isUndefined(licenseInfo) ? '' : L10n.localizedCount(licenseInfo.totalExplorerUsers)
          })}
        </span>
        <span className={LicenseInfoBarItem}>
          {isCoreBasedLicense ? Messages.licenseViewerUnlimited() :
            Messages.licenseViewerCounts({
            USED_COUNT_LOC: _.isUndefined(licenseInfo) ? '' : L10n.localizedCount(licenseInfo.usedViewerUsers),
            TOTAL_COUNT_LOC: _.isUndefined(licenseInfo) ? '' : L10n.localizedCount(licenseInfo.totalViewerUsers)
          })}
        </span>
        <span className={LicenseInfoBarItem}>
          {Messages.unlicensedCount({
            COUNT_LOC: _.isUndefined(licenseInfo) ? '' : L10n.localizedCount(licenseInfo.totalUnlicensedUsers)
          })}
        </span>
      </>}
    </div>
  );
}

const cssPrefix = 'LicenseInfoBar';
const LicenseInfoBarContainer = cssClass(cssPrefix, 'container', {
  height: '2em',
  overflowY: 'hidden',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  paddingTop: '4px',
  $nest: {
    '&>*': {
      paddingRight: TabStyles.Spacing.MediumSpace,
      paddingLeft: TabStyles.Spacing.MediumSpace,
    },
    '&>*:last-child': {
      paddingRight: 0,
    },
    '&>*:first-child': {
      paddingLeft: 0,
    }
  }
});

const LicenseInfoBarItem = cssClass(cssPrefix, 'item', {
  minWidth: 0, // 0 is the default
  color: CommonStyles.Colors.Grey102,
  fontSize: '14px',
});
